import SVG from "react-inlinesvg";
import { useIntl } from "react-intl";
import { SimpleEntityGridColumn } from "src/app/components/grid/SimpleEntityGrid";
import { toAbsoluteUrl } from "../../../../_metronic/helpers";
import { CellFlag, CellLinks, CellTags, UserCell } from "src/app/components";
import moment from "moment";
import { columnParForDates } from "src/app/components/grid/GridHelpers";

export default function useStockGridColumns() {
    const { formatMessage } = useIntl();
    const gridColumns: Array<SimpleEntityGridColumn> = [
        {
            field: "id",
            filter: "agNumberColumnFilter",
            cellClass: "number-cell",
            minWidth: 60,
            maxWidth: 60,
            hide: true,
        },
        {
            field: "publishedAt",
            headerName: formatMessage({
                id: "stock.grid.col.publishedAt",
            }),
            ...columnParForDates("publishedAt"),
            minWidth: 90,
            maxWidth: 90,
            cellClassRules: {
                "order-fail": ({ data }: any) =>
                    moment(data.publishedAt).unix() <= moment().subtract(1, "d").unix(),
            },
        },
        {
            field: "manager",
            filter: "agSetColumnFilter",
            headerName: formatMessage({ id: "content.grid.col.manager" }),
            valueGetter: ({ data }: any) => (data?.manager ? data.manager : ""),
            cellRenderer: ({ data }: any) => <UserCell user={data.manager} />,
            autoHeight: true,
            minWidth: 50,
            maxWidth: 50,
        },
        {
            field: "lang",
            autoHeight: true,
            filter: "agSetColumnFilter",
            valueGetter: ({ data }) => (data?.lang ? `${data.lang}` : ""),
            cellRenderer: ({ data }: any) => <CellFlag lang={data.lang} />,
            minWidth: 50,
            maxWidth: 50,
        },
        {
            field: "moneySite",
            filter: "agSetColumnFilter",
            headerName: formatMessage({ id: "content.grid.col.moneySite" }),
            valueGetter: ({ data }) =>
                data?.platform
                    ? data?.platform
                    : data?.moneySite
                    ? `${data?.moneySite} (${data?.pbn})`
                    : "N/A",
        },
        {
            field: "rd",
            filter: "agSetColumnFilter",
            headerName: formatMessage({ id: "content.grid.col.publishedOn" }),
        },
        {
            field: "thematic",
            filter: "agSetColumnFilter",
            headerName: formatMessage({ id: "content.grid.col.thematic" }),
            minWidth: 107,
            maxWidth: 107,
        },
        {
            field: "contentType",
            filter: "agSetColumnFilter",
            headerName: formatMessage({ id: "content.grid.col.contentType" }),
            valueGetter: ({ data }: any) =>
                `${formatMessage({
                    id: `content.grid.col.contentType.${data.contentType}`,
                })} ${formatMessage({
                    id: `content.grid.col.writingType.${data.writingType}`,
                })}`,
            minWidth: 100,
            maxWidth: 100,
        },
        {
            field: "wordCount",
            filter: "agNumberColumnFilter",
            cellClass: "number-cell",
            headerName: formatMessage({ id: "content.grid.col.words" }),
            minWidth: 65,
            maxWidth: 65,
        },
        {
            field: "orderPrice",
            filter: "agNumberColumnFilter",
            cellClass: "number-cell",
            headerName: formatMessage({ id: "content.grid.col.price" }),
            valueFormatter: ({ data }: any) => `${data.orderPrice} €`,
            minWidth: 65,
            maxWidth: 65,
        },
        {
            field: "keywords",
            autoHeight: true,
            filter: "agTextColumnFilter",
            headerName: formatMessage({ id: "content.grid.col.keyword" }),
            valueGetter: ({ data }: any) => data?.keywords,
            cellRenderer: ({ data }: any) => <CellTags tags={data?.keywords} />,
        },
        {
            field: "links",
            autoHeight: true,
            filter: "agTextColumnFilter",
            headerName: formatMessage({ id: "content.grid.col.links" }),
            valueGetter: ({ data }: any) =>
                data?.links
                    ? data.links[1]
                        ? `${data.links[0].url} (${data.links[0].anchor})\n ${data.links[1].url} (${data.links[1].anchor})`
                        : `${data.links[0].url} (${data.links[0].anchor})`
                    : "",
            cellRenderer: ({ data }: any) => <CellLinks links={data.links} />,
        },
        {
            field: "writerNeeded",
            headerName: formatMessage({ id: "content.grid.col.writerNeeded" }),
            valueGetter: ({ data }: any) =>
                data?.writerNeeded
                    ? formatMessage({ id: "common.field.no" })
                    : data?.platform
                    ? formatMessage({ id: "common.field.yes" })
                    : formatMessage({ id: "common.field.no" }),
            cellRenderer: ({ data }: any) =>
                data.writerNeeded ? (
                    <div className="w-100 h-100 d-flex justify-content-center">
                        <SVG
                            fill="var(--kt-text-danger)"
                            width="16px"
                            src={toAbsoluteUrl("/media/icons/duotune/general/cross.svg")}
                        />
                    </div>
                ) : data?.platform ? (
                    <div className="w-100 h-100 d-flex justify-content-center">
                        <SVG
                            width="16px"
                            fill="var(--kt-text-success)"
                            src={toAbsoluteUrl("/media/icons/duotune/general/tick.svg")}
                        />
                    </div>
                ) : (
                    <div className="w-100 h-100 d-flex justify-content-center">
                        <SVG
                            fill="var(--kt-text-danger)"
                            width="16px"
                            src={toAbsoluteUrl("/media/icons/duotune/general/cross.svg")}
                        />
                    </div>
                ),
            filter: "agSetColumnFilter",
            minWidth: 65,
            maxWidth: 65,
        },
        {
            field: "commentForWriter",
            filter: "agTextColumnFilter",
            headerName: formatMessage({ id: "content.grid.col.commentForWriter" }),
            hide: true,
        },
        {
            field: "createdAt",
            headerName: formatMessage({
                id: "content.grid.col.createdAt",
            }),
            ...columnParForDates("createdAt"),
            hide: true,
            minWidth: 130,
            maxWidth: 130,
        },
        {
            field: "updatedAt",
            headerName: formatMessage({
                id: "content.grid.col.updatedAt",
            }),
            ...columnParForDates("updatedAt"),
            hide: true,
            maxWidth: 130,
            minWidth: 130,
        },
    ];

    return gridColumns;
}
